import { Anchor, AppShell, Group, Image, Stack, Space } from '@mantine/core';
import { MainContainer } from './MainContainer';
import { PageHeader } from './PageHeader';
import { useAuth0 } from '@auth0/auth0-react';
import { clearIssuer, setSkipSessionProbe } from '@/lib/auth';

export type UnauthReason = 'notBelongsAnyTenant' | 'emailAlreadyInUse' | 'unknown';

type Props = {
  reason: UnauthReason;
};

export function Unauthorized({ reason }: Props) {
  const { logout } = useAuth0();

  const handleLogout = () => {
    setSkipSessionProbe();
    clearIssuer();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  return (
    <AppShell header={{ height: 48 }}>
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group justify="start">
            <Anchor href="/">
              <Image hiddenFrom="md" src="/plm_b_full_rgb.png" w="32" h="32" alt="plamo" />
              <Image visibleFrom="md" src="/plm_a_full_rgb.png" w="100" h="32" alt="plamo" />
            </Anchor>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <MainContainer>
          {reason === 'notBelongsAnyTenant' && (
            <>
              <PageHeader title="PLaMo APIのご利用には、テナントへのログインが必要です。" />
              <Stack mb="xl">
                <p>ログイン時にテナントが指定されていないため、ご利用いただけません。</p>
                <p>
                  PLaMo APIのテナントをお持ちでない方は、
                  <Anchor href="https://plamo.preferredai.jp/api">こちら</Anchor>
                  より利用登録をしてください。
                  <br />
                  テナントをお持ちの方は、テナント管理者に招待を依頼してください。
                </p>
                <p>
                  所属するテナントがあるにも関わらずこのエラーが表示される場合は、一度ログアウトし、再度ログインしてください。
                </p>
              </Stack>
            </>
          )}
          {reason === 'emailAlreadyInUse' && (
            <>
              <PageHeader title="ログインに失敗しました。" />
              <Stack mb="xl">
                <p>このメールアドレスは既に使用されています。</p>
                <p>
                  同一のメールアドレスを用いて、複数の認証方式を利用することはできません。
                  <br />
                  以前と同じ認証方式を使用するか、別のメールアドレスをご利用ください。
                </p>
              </Stack>
            </>
          )}
          {reason === 'unknown' && (
            <>
              <PageHeader title="ログインに失敗しました。" />
              <Stack mb="xl">
                <p>
                  時間をおいて、再度ログインをお試しください。
                  <br />
                  それでも解決しない場合は、
                  <Anchor href="https://support.plamo.preferredai.jp/">ヘルプセンター</Anchor>
                  にお問い合わせください。
                </p>
              </Stack>
            </>
          )}
          <Group gap="xl">
            <Anchor href="https://plamo.preferredai.jp/api">
              <p>ランディングページに戻る</p>
            </Anchor>
            <Anchor onClick={handleLogout}>ログアウト</Anchor>
          </Group>
        </MainContainer>
      </AppShell.Main>
    </AppShell>
  );
}
